import React from 'react';
import { Button, FileUploader } from 'pink-lava-ui';
import { Flex, Upload, message, type UploadProps } from 'antd';
import { UploadCloudIcon } from 'lucide-react';

interface UploadDraggerProps extends UploadProps {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  isCompact?: boolean;
  maxFileSizeMB?: number;
}

const UploadDragger: React.FC<UploadDraggerProps> = ({
  title = 'Drag & Drop Files Here Max 5mb',
  description = 'OR',
  icon,
  isCompact = false,
  maxFileSizeMB = 5,
  accept,
  beforeUpload,
  fileList,
  ...restProps
}) => {
  if (fileList?.length && !isCompact) return null;

  const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
  const enhancedBeforeUpload: UploadProps['beforeUpload'] = (file, fileListArg) => {
    if (accept) {
      const acceptedTypes = accept.split(',').map(type => type.trim().toLowerCase());
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
      const fileMimeType = (file.type || '').toLowerCase();

      const isValidType = acceptedTypes.some(acceptedType => {
        if (acceptedType.startsWith('.')) {
          return fileExtension === acceptedType;
        }
        if (acceptedType.includes('/')) {
          const normalizedType = acceptedType.replace('*', '').trim();
          return fileMimeType === acceptedType || fileMimeType.startsWith(normalizedType);
        }
        if (acceptedType === 'image') {
          return fileMimeType.startsWith('image/');
        }
        if (acceptedType === 'video') {
          return fileMimeType.startsWith('video/');
        }
        if (acceptedType === 'audio') {
          return fileMimeType.startsWith('audio/');
        }
        return false;
      });

      if (!isValidType) {
        message.error(`Tipe file tidak didukung. Hanya file dengan format ${accept} yang diizinkan.`);
        return Upload.LIST_IGNORE;
      }
    }

    if (file.size > maxFileSizeBytes) {
      message.error(`Ukuran file harus kurang dari ${maxFileSizeMB}MB`);
      return Upload.LIST_IGNORE;
    }

    if (beforeUpload) {
      return beforeUpload(file, fileListArg);
    }

    return true;
  };

  return (
    <FileUploader
      type="drag"
      {...restProps}
      accept={accept}
      beforeUpload={enhancedBeforeUpload}
      fileList={fileList}
      showUploadList={false}
    >
      <Flex gap={8} vertical align="center" className="p-5 text-center text-[#888888]">
        {isCompact ? (
          <Flex gap={8} vertical align="center">
            <p className="text-base text-[#888888]">Drag & Drop or Browse Files</p>
          </Flex>
        ) : (
          <>
            {icon || <UploadCloudIcon color="#888888" height={72} width={72} />}
            <p className="text-base ">{title}</p>
            <p className="text-base text-[#888888]">{description}</p>
            <Button shape="round" type="tertiary">
              Browse File
            </Button>
          </>
        )}
      </Flex>
    </FileUploader>
  );
};

export default UploadDragger;
